import { useParams, Link, Navigate } from "react-router-dom";
import { modules } from "@/data/modules";
import { CheckCircle2, ArrowRight } from "lucide-react";
import FAQAccordion from "@/components/sections/FAQAccordion";

export default function ModuleLanding() {
  const { slug } = useParams();
  const m = modules.find((x) => x.slug === slug);
  if (!m) return <Navigate to="/features" replace />;
  const I = m.icon;

  return (
    <div data-testid={`page-module-${slug}`}>
      <section className="py-20 md:py-24 hero-grid-bg">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-[#1F1E4E]">Home</Link>
            <span>/</span>
            <Link to="/features" className="hover:text-[#1F1E4E]">Features</Link>
            <span>/</span>
            <span className="text-[#1F1E4E] font-semibold">{m.name}</span>
          </div>
          <div className="h-14 w-14 rounded-2xl bg-[#E6F6EC] flex items-center justify-center mb-6">
            <I className="h-7 w-7 text-[#00A651]" />
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tighter text-[#1F1E4E] leading-[1.05]">{m.name} Software</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">{m.tagline}. {m.solution}</p>
          <div className="mt-8 flex gap-3 flex-wrap">
            <Link to="/contact?intent=book_demo" data-testid={`module-cta-demo-${slug}`} className="bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-6 py-3 inline-flex items-center gap-2">
              Book Demo <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact?intent=request_quote" className="border-2 border-[#1F1E4E] text-[#1F1E4E] font-bold rounded-lg px-6 py-3">Request Quote</Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-slate-200 p-7">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Business Problem</div>
            <p className="text-slate-700 leading-relaxed">{m.problem}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 p-7">
            <div className="text-xs font-bold uppercase tracking-widest text-[#00A651] mb-3">Our Solution</div>
            <p className="text-slate-700 leading-relaxed">{m.solution}</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-[#1F1E4E] mb-8">Key Benefits</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {m.benefits.map((b, i) => (
              <div key={i} className="flex gap-3 bg-white rounded-xl border border-slate-200 p-5">
                <CheckCircle2 className="h-5 w-5 text-[#00A651] shrink-0 mt-0.5" />
                <span className="text-slate-700">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-[#1F1E4E] mb-8">Detailed Features</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {m.features.map((f, i) => (
              <div key={i} className="flex gap-2 text-slate-700"><span className="h-1.5 w-1.5 rounded-full bg-[#00A651] mt-2 shrink-0" />{f}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#1F1E4E] text-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-xs font-bold uppercase tracking-widest text-[#00A651] mb-3">Business Use Cases</div>
          <h2 className="text-3xl font-extrabold mb-8">How teams use {m.name}</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {m.useCases.map((u, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 text-white/85">{u}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-extrabold text-[#1F1E4E] mb-8">Common Questions</h2>
          <FAQAccordion limit={6} />
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h3 className="text-2xl md:text-3xl font-extrabold text-[#1F1E4E]">See {m.name} in action</h3>
          <Link to="/contact?intent=book_demo" data-testid={`module-bottom-cta-${slug}`} className="inline-flex mt-6 bg-[#00A651] hover:bg-[#008A43] text-white font-bold rounded-lg px-6 py-3">Book a Personalized Demo</Link>
        </div>
      </section>
    </div>
  );
}
